import { useId, useRef, type ChangeEvent } from 'react'
import { Button, type ButtonProps } from './button'

/**
 * Botão que abre o seletor de arquivo do aparelho (ou a câmera, com `capture`), com o desenho de
 * `Button`. O `<input type="file">` fica invisível e o rótulo diz o que vai acontecer ("Tirar foto",
 * "Escolher da galeria"). O mesmo arquivo pode ser escolhido duas vezes seguidas: o campo é limpo
 * depois de cada escolha.
 */
export interface FileButtonProps extends Omit<ButtonProps, 'onChange' | 'asChild' | 'type'> {
  /** Os tipos aceitos, como no atributo `accept` ("image/jpeg,image/png"). */
  accept?: string
  /** Abre a câmera do aparelho em vez da galeria: `environment` (traseira) ou `user`. */
  capture?: 'environment' | 'user'
  multiple?: boolean
  onFiles: (files: File[]) => void
}

export function FileButton({
  accept,
  capture,
  multiple = false,
  onFiles,
  children,
  disabled,
  loading,
  id: idProp,
  ...props
}: FileButtonProps) {
  const generated = useId()
  const id = idProp ?? generated
  const input = useRef<HTMLInputElement>(null)

  function change(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length > 0) onFiles(files)
  }

  return (
    <>
      <input
        ref={input}
        id={id}
        data-slot="file-input"
        type="file"
        accept={accept}
        capture={capture}
        multiple={multiple}
        disabled={disabled || loading}
        onChange={change}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />
      <Button
        {...props}
        disabled={disabled}
        loading={loading}
        onClick={(event) => {
          props.onClick?.(event)
          if (!event.defaultPrevented) input.current?.click()
        }}
      >
        {children}
      </Button>
    </>
  )
}
